import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import { timeToMinutes, formatTimeDisplay, getHourOptions, getMinuteOptions, to12Hour, to24Hour } from '@/lib/timeUtils';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';

export interface TimePickerProps {
  value?: string | null; // "HH:mm" format (24h internal)
  onChange?: (time: string | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  use24Hour?: boolean; // override auto-detect from locale
  minuteStep?: number; // default 5
  minTime?: string; // "HH:mm"
  maxTime?: string; // "HH:mm"
  className?: string;
}

export function TimePicker({
  value,
  onChange,
  placeholder,
  label,
  error,
  hint,
  disabled,
  use24Hour,
  minuteStep = 5,
  minTime,
  maxTime,
  className,
}: TimePickerProps) {
  const localeSettings = useLocaleSettings();
  const is24Hour = useMemo(
    () => (use24Hour !== undefined ? use24Hour : localeSettings.timeFormat !== 'hh:mm A'),
    [use24Hour, localeSettings.timeFormat]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const containerRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const inputId = React.useId();

  // Parse current value
  const parsedHour = value ? parseInt(value.split(':')[0], 10) : null;
  const parsedMinute = value ? parseInt(value.split(':')[1], 10) : null;

  // Sync period from value
  useEffect(() => {
    if (parsedHour !== null && !is24Hour) {
      setPeriod(parsedHour >= 12 ? 'PM' : 'AM');
    }
  }, [parsedHour, is24Hour]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Scroll selected items into view when popover opens
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      hourListRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
      minuteListRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
    });
  }, [isOpen]);

  const hours = useMemo(() => getHourOptions(is24Hour), [is24Hour]);
  const minutes = useMemo(() => getMinuteOptions(minuteStep), [minuteStep]);

  const isTimeInRange = useCallback(
    (h: number, m: number): boolean => {
      const total = h * 60 + m;
      if (minTime && total < timeToMinutes(minTime)) return false;
      if (maxTime && total > timeToMinutes(maxTime)) return false;
      return true;
    },
    [minTime, maxTime]
  );

  const emitChange = useCallback(
    (h24: number, m: number) => {
      if (!isTimeInRange(h24, m)) return;
      const timeStr = `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      onChange?.(timeStr);
    },
    [onChange, isTimeInRange]
  );

  const handleHourClick = useCallback(
    (hour: number) => {
      const h24 = is24Hour ? hour : to24Hour(hour, period);
      const m = parsedMinute ?? 0;
      emitChange(h24, m);
    },
    [is24Hour, period, parsedMinute, emitChange]
  );

  const handleMinuteClick = useCallback(
    (minute: number) => {
      const h24 = parsedHour ?? (is24Hour ? 0 : to24Hour(12, period));
      emitChange(h24, minute);
    },
    [parsedHour, is24Hour, period, emitChange]
  );

  const handlePeriodChange = useCallback(
    (newPeriod: 'AM' | 'PM') => {
      setPeriod(newPeriod);
      if (parsedHour === null) return;
      const currentH12 = to12Hour(parsedHour);
      const newH24 = to24Hour(currentH12, newPeriod);
      const m = parsedMinute ?? 0;
      emitChange(newH24, m);
    },
    [parsedHour, parsedMinute, emitChange]
  );

  const handleClear = useCallback(() => {
    onChange?.(null);
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) setIsOpen((prev) => !prev);
      }
    },
    [disabled]
  );

  const hasError = Boolean(error);
  const displayValue = value ? formatTimeDisplay(value, is24Hour) : '';

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        id={inputId}
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={hasError}
        aria-describedby={
          hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
        }
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError && 'border-error-500 focus:ring-error-500',
          !displayValue && 'text-muted-foreground'
        )}
      >
        <span>{displayValue || placeholder || (is24Hour ? 'HH:mm' : 'hh:mm AM/PM')}</span>
        <Clock className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-popover mt-1 w-auto max-w-[280px] rounded-md border border-border bg-popover text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2'
          )}
        >
          <div className="flex">
            {/* Hours column */}
            <div
              ref={hourListRef}
              role="listbox"
              aria-label="Hours"
              className="max-h-56 overflow-y-auto border-r border-border py-1 scrollbar-thin"
              style={{ minWidth: is24Hour ? '3.5rem' : '3.5rem' }}
            >
              {hours.map((hour) => {
                const h24 = is24Hour ? hour : to24Hour(hour, period);
                // Check if any minute in this hour is in range
                const hourDisabled = !minutes.some((m) => isTimeInRange(h24, m));
                const isSelected = is24Hour
                  ? parsedHour === hour
                  : parsedHour !== null && to12Hour(parsedHour) === hour;

                return (
                  <button
                    key={hour}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    disabled={hourDisabled}
                    onClick={() => handleHourClick(hour)}
                    className={cn(
                      'w-full px-3 py-1.5 text-sm text-center cursor-default select-none text-popover-foreground',
                      'hover:bg-primary-50 hover:text-primary-700',
                      'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                      isSelected && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white',
                      hourDisabled && 'opacity-40 pointer-events-none'
                    )}
                  >
                    {is24Hour
                      ? String(hour).padStart(2, '0')
                      : String(hour)}
                  </button>
                );
              })}
            </div>

            {/* Minutes column */}
            <div
              ref={minuteListRef}
              role="listbox"
              aria-label="Minutes"
              className={cn(
                'max-h-56 overflow-y-auto py-1 scrollbar-thin',
                !is24Hour && 'border-r border-border'
              )}
              style={{ minWidth: '3.5rem' }}
            >
              {minutes.map((minute) => {
                const h24 = parsedHour ?? (is24Hour ? 0 : to24Hour(12, period));
                const minuteDisabled = !isTimeInRange(h24, minute);
                const isSelected = parsedMinute === minute;

                return (
                  <button
                    key={minute}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    disabled={minuteDisabled}
                    onClick={() => handleMinuteClick(minute)}
                    className={cn(
                      'w-full px-3 py-1.5 text-sm text-center cursor-default select-none text-popover-foreground',
                      'hover:bg-primary-50 hover:text-primary-700',
                      'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                      isSelected && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white',
                      minuteDisabled && 'opacity-40 pointer-events-none'
                    )}
                  >
                    {String(minute).padStart(2, '0')}
                  </button>
                );
              })}
            </div>

            {/* AM/PM toggle (12h mode only) */}
            {!is24Hour && (
              <div className="flex flex-col justify-center gap-1 px-2 py-1">
                {(['AM', 'PM'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePeriodChange(p)}
                    className={cn(
                      'rounded-md px-2 py-1.5 text-xs font-medium cursor-default select-none text-popover-foreground',
                      'hover:bg-primary-50 hover:text-primary-700',
                      'focus:outline-none focus:bg-primary-50 focus:text-primary-700',
                      period === p && 'bg-primary-500 text-white hover:bg-primary-500 hover:text-white focus:bg-primary-500 focus:text-white'
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer with clear */}
          {value && (
            <div className="border-t border-border px-2 py-1.5">
              <button
                type="button"
                onClick={handleClear}
                className="w-full rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-primary-50 hover:text-primary-700 text-center"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className="mt-1 text-sm text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}
